import React, {useCallback, useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import {GIFT_SHOP_ROOT_BOTTOM_NAVBAR_HEIGHT} from 'consts'
import {useTranslation} from 'i18n'
import {GiftShopTemplateOptionsModal, GiftShopTemplateReportModal} from 'pages'
import {requestData} from 'services'
import {
  ForkygramPostActionOptions,
  ForkygramPostActionOptionsProfileOther,
  ForkygramReportCategoriesResponse,
} from 'types'
import {
  getCollaboratorMember,
  showModalLimitedAccess,
  showModalReprocessKyc,
  showSnackbar,
  useHistory,
} from 'utils'
import {useWindowLayout, useWindowMode} from 'windows'
import {ConfirmationModal} from 'common/components'
import {useSelector} from 'lib/redux'
import {
  GiftShopTemplateOptionsContent,
  GiftShopTemplateOptionsItem,
  GiftShopTemplateShareModal,
} from '../../../../gift-shop'
import {ForkygramTemplatePostContent} from '../../../../forkygram'
import {TemplateProfileSelfOptionModal} from '../SelfOptionModal'
import {TemplateProfileDetailItemProps} from './TemplateProfileDetailItemProps'

const StyledContainer = styled.div`
  display: flex;
  width: 100%;
`

export default function TemplateProfileDetailItem({
  data,
  componentRef,
  onDelete,
  onEdit,
  hideBottomNavbar,
  isFeed,
  self,
  scrollPosition,
  index,
  stateVideoPause,
  stateMute,
  onReported,
  onPin,
  onFollowed,
  onCollabRemoved,
  setVideoPlaying,
}: TemplateProfileDetailItemProps) {
  const {translate} = useTranslation()
  const history = useHistory()
  const [selfModal, setSelfModal] = useState<ForkygramPostActionOptions>('none')
  const {height} = useWindowLayout()
  const pause = stateVideoPause[0]
  const mode = useWindowMode()
  const contentHeight =
    mode === 'website' ? height : height - GIFT_SHOP_ROOT_BOTTOM_NAVBAR_HEIGHT
  const scrollPos = scrollPosition + 0.2 * contentHeight
  const focused = Math.floor(scrollPos / contentHeight) === index
  const active = focused && !pause

  const [modal, setModal] = useState<ForkygramPostActionOptionsProfileOther>(
    'none',
  )
  const user = useSelector('user')
  const {kycStatus} = useSelector('yuserActivationState')
  const {status: similarStatus} = useSelector('similarAccountStatus')
  const {status: childStatus} = useSelector('parentalConsentState')
  const stateRot = useState(0)
  const [rot, setRot] = stateRot
  const currentRot = useRef(0)

  const {collaboratorOwner, collaboratorSelf} = getCollaboratorMember(
    data.collaborator_members,
  )

  const handleToggleModal = useCallback(() => {
    setSelfModal('none')
    setModal('none')
  }, [])

  const handleBlockAccount = useCallback(() => {
    user &&
      requestData('forkygram_post_block_account', {
        useDefaultMessage: true,
        actionType: 'execute',
        data: {
          blocked_id: collaboratorOwner.member_id,
        },
        onRequestSuccess: ({status}) => {
          status === 200 &&
            showSnackbar(translate('forkygram:blockYuserAccSuccess'))
        },
      })
  }, [collaboratorOwner.member_id, translate, user])

  const handleReportPost = useCallback(
    (category: ForkygramReportCategoriesResponse, description?: string) => {
      requestData('forkygram_post_report_posts', {
        data: {
          category_id: category.id,
          description,
          reported_id: data.id,
          reported_member_id: collaboratorOwner.member_id,
        },
        onRequestReceived: () => handleToggleModal(),
        onRequestFailed: () =>
          showSnackbar(translate('forkygram:reportPostFailed')),
        onRequestSuccess: ({status}) => {
          onReported(data.id)
          showSnackbar(
            translate(
              status === 200
                ? 'forkygram:reportPostSuccess'
                : 'forkygram:reportPostFailed',
            ),
          )
        },
      })
    },
    [
      collaboratorOwner.member_id,
      data.id,
      handleToggleModal,
      onReported,
      translate,
    ],
  )

  const handleRemoveCollaboration = useCallback(() => {
    if (!collaboratorSelf) return
    requestData('forkygram_delete_collaborator', {
      useDefaultMessage: true,
      data: {post_id: data.id},
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          showSnackbar(translate('tree:removeCollaborationSuccessMessage'))
          onCollabRemoved(collaboratorSelf.member_id)
        }
      },
    })
  }, [collaboratorSelf, data.id, onCollabRemoved, translate])

  const handleFeatureDisabled = useCallback(() => {
    handleToggleModal()
    if (childStatus) {
      showModalLimitedAccess()
      return
    }
    if (kycStatus === 'onhold') {
      if (similarStatus === 'CHOOSE_SELF') {
        return showModalReprocessKyc()
      }
      return history.push('giftshop_similar_identity', {})
    }
    if (kycStatus === 'unregistered') {
      history.push('giftshop_robopet_activation_selfie', {})
      return
    }
    history.push('giftshop_robopet', {})
  }, [childStatus, handleToggleModal, history, kycStatus, similarStatus])

  const handleShare = useCallback(() => {
    if (
      childStatus ||
      !(kycStatus === 'verified' || kycStatus === 'verified_check')
    ) {
      handleToggleModal()
      handleFeatureDisabled()
      return
    }
    setModal('share')
  }, [childStatus, handleFeatureDisabled, handleToggleModal, kycStatus])

  const handleSelfShare = useCallback(() => {
    if (childStatus) {
      handleToggleModal()
      handleFeatureDisabled()
      return
    }
    setSelfModal('share')
  }, [childStatus, handleFeatureDisabled, handleToggleModal])

  const handleRotateContent = useCallback(
    (dir: 'left' | 'right' | 'flip') => {
      // preview set to CCW
      if (dir === 'flip') {
        setRot((prev) => (prev - 180) % 360)
        currentRot.current += 180
      } else if (dir === 'left') {
        setRot((prev) => (prev - 90) % 360)
        currentRot.current += 90
      } else if (dir === 'right') {
        setRot((prev) => (prev - 270) % 360)
        currentRot.current += 270
      }
    },
    [setRot],
  )

  const handleFinalizeRotateModal = useCallback(() => {
    handleToggleModal()
    if (currentRot.current % 360 !== 0) {
      requestData('forkygram_rotate_post', {
        data: {post_id: data.id, rotation: currentRot.current % 360},
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            showSnackbar(translate('global:success'))
          } else {
            setRot(rot + (currentRot.current % 360))
            showSnackbar(
              translate('global:messageError', {context: `code-${status}`}),
            )
          }
          currentRot.current = 0
        },
      })
    }
  }, [data.id, handleToggleModal, rot, setRot, translate])

  const handleRenderRotateModal = useMemo(
    () => (
      <GiftShopTemplateOptionsContent
        visible
        toggleModal={handleFinalizeRotateModal}>
        <GiftShopTemplateOptionsItem
          icon="rotate-left"
          text={translate('global:rotateLeft')}
          onClickItem={() => {
            handleRotateContent('left')
          }}
        />
        <GiftShopTemplateOptionsItem
          icon="rotate-right"
          text={translate('global:rotateRight')}
          onClickItem={() => {
            handleRotateContent('right')
          }}
        />
        <GiftShopTemplateOptionsItem
          icon="flip-camera"
          text={translate('global:flipVertical')}
          onClickItem={() => {
            handleRotateContent('flip')
          }}
        />
      </GiftShopTemplateOptionsContent>
    ),
    [handleFinalizeRotateModal, handleRotateContent, translate],
  )

  const handleRenderOptionsModal = useMemo(
    () => (
      <GiftShopTemplateOptionsModal
        isProfilePost
        onShare={handleShare}
        onBlock={() =>
          !(kycStatus === 'verified' || kycStatus === 'verified_check')
            ? handleFeatureDisabled()
            : setModal('block')
        }
        onDelete={
          data.can_delete
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('delete')
            : undefined
        }
        onEdit={
          data.can_edit
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : onEdit()
            : undefined
        }
        onReport={
          !data.can_edit
            ? () =>
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('report')
            : undefined
        }
        onRemoveCollab={
          collaboratorSelf
            ? () => {
                !(kycStatus === 'verified' || kycStatus === 'verified_check')
                  ? handleFeatureDisabled()
                  : setModal('remove-collaboration')
              }
            : undefined
        }
        toggleModal={handleToggleModal}
        onRotate={() => setModal('rotate')}
      />
    ),
    [
      collaboratorSelf,
      data.can_delete,
      data.can_edit,
      handleFeatureDisabled,
      handleShare,
      handleToggleModal,
      kycStatus,
      onEdit,
    ],
  )

  const handleRenderShareModal = useMemo(
    () => (
      <GiftShopTemplateShareModal
        type="post"
        targetId={data.id}
        subTargetId={collaboratorOwner.member_id}
        caption={data.caption}
        toggleModal={handleToggleModal}
      />
    ),
    [collaboratorOwner.member_id, data.caption, data.id, handleToggleModal],
  )

  const handleRenderReportModal = useMemo(
    () => (
      <GiftShopTemplateReportModal
        type="post"
        translationKey="giftShop:reportReason"
        onSubmit={handleReportPost}
        toggleModal={() => {
          handleToggleModal()
          showSnackbar(translate('forkygram:reportCancel'))
        }}
      />
    ),
    [handleReportPost, handleToggleModal, translate],
  )

  const handleRenderBlockYuserModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        text={translate('forkygram:blockYuserAccText')}
        title={translate('forkygram:blockYuserAcc')}
        cancelText={translate('global:cancel')}
        onConfirm={handleBlockAccount}
        toggleModal={handleToggleModal}
      />
    ),
    [handleBlockAccount, handleToggleModal, translate],
  )

  const handleRenderRemoveCollabModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('tree:removeCollaboration')}
        text={translate('tree:removeCollaborationDescription')}
        onConfirm={handleRemoveCollaboration}
        toggleModal={handleToggleModal}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:remove')}
      />
    ),
    [handleRemoveCollaboration, handleToggleModal, translate],
  )

  const handleRenderDeleteModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('forkygram:deletePostTitle')}
        text={translate('forkygram:deletePostMessage')}
        onConfirm={onDelete}
        toggleModal={handleToggleModal}
        cancelText={translate('global:cancel')}
        confirmText={translate('global:delete')}
      />
    ),
    [handleToggleModal, onDelete, translate],
  )

  const handleRenderMoreModal = useMemo(
    () => (
      <TemplateProfileSelfOptionModal
        visible
        isPinned={data.is_pinned}
        toggleModal={handleToggleModal}
        onClickDelete={
          data.can_delete ? () => setSelfModal('delete') : undefined
        }
        onClickEdit={data.can_edit ? onEdit : undefined}
        onClickPin={() => onPin(data.id, data.is_pinned)}
        onClickShare={handleSelfShare}
        onRotate={() => setSelfModal('rotate')}
        onClickRemoveCollab={
          collaboratorSelf
            ? () => setSelfModal('remove-collaboration')
            : undefined
        }
      />
    ),
    [
      collaboratorSelf,
      data.can_delete,
      data.can_edit,
      data.id,
      data.is_pinned,
      handleSelfShare,
      handleToggleModal,
      onEdit,
      onPin,
    ],
  )

  const handleRenderSelfModal = useMemo(() => {
    switch (selfModal) {
      case 'more':
        return handleRenderMoreModal
      case 'delete':
        return handleRenderDeleteModal
      case 'share':
        return handleRenderShareModal
      case 'remove-collaboration':
        return handleRenderRemoveCollabModal
      case 'rotate':
        return handleRenderRotateModal
      case 'none':
        return null
    }
  }, [
    selfModal,
    handleRenderMoreModal,
    handleRenderDeleteModal,
    handleRenderShareModal,
    handleRenderRemoveCollabModal,
    handleRenderRotateModal,
  ])

  const handleRenderOtherModal = useMemo(() => {
    switch (modal) {
      case 'block':
        return handleRenderBlockYuserModal
      case 'delete':
        return handleRenderDeleteModal
      case 'options':
        return handleRenderOptionsModal
      case 'report':
        return handleRenderReportModal
      case 'remove-collaboration':
        return handleRenderRemoveCollabModal
      case 'share':
        return handleRenderShareModal
      case 'rotate':
        return handleRenderRotateModal
      case 'none':
        return null
    }
  }, [
    handleRenderBlockYuserModal,
    handleRenderDeleteModal,
    handleRenderOptionsModal,
    handleRenderRemoveCollabModal,
    handleRenderReportModal,
    handleRenderRotateModal,
    handleRenderShareModal,
    modal,
  ])

  const handleClickMore = useCallback(() => {
    if (self) setSelfModal('more')
    else setModal('options')
  }, [self])

  const handleRenderContent = useMemo(
    () => (
      <ForkygramTemplatePostContent
        data={data}
        isFeed={isFeed}
        active={active}
        focused={focused}
        hideBottomNavbar={hideBottomNavbar}
        stateMute={stateMute}
        stateVideoPause={stateVideoPause}
        setVideoPlaying={setVideoPlaying}
        onClickMore={handleClickMore}
        onFollowed={onFollowed}
        stateRot={stateRot}
      />
    ),
    [
      active,
      data,
      focused,
      handleClickMore,
      hideBottomNavbar,
      isFeed,
      onFollowed,
      setVideoPlaying,
      stateMute,
      stateRot,
      stateVideoPause,
    ],
  )

  return (
    <StyledContainer ref={componentRef}>
      {handleRenderOtherModal}
      {handleRenderSelfModal}
      {handleRenderContent}
    </StyledContainer>
  )
}
